import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import _ from "lodash";

// ---- helpers: timezone-safe (Pacific/Auckland) without dayjs ----
const NZ_TZ = "Pacific/Auckland";

/** Convert a Date to a "zoned" Date where UTC-getters reflect local fields in the given time zone */
function toZoned(date, timeZone = NZ_TZ) {
  // Use Intl to get the local parts (y,m,d,h,m,s) for that timeZone
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })
    .formatToParts(date)
    .reduce((acc, p) => ((acc[p.type] = p.value), acc), {});
  // Build a Date whose *UTC* fields equal the local (NZ) fields
  return new Date(
    Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour),
      Number(parts.minute),
      Number(parts.second)
    )
  );
}

function startOfWeekNZ(anyDateUtc) {
  const nz = toZoned(anyDateUtc);
  // getUTCDay: 0=Sun..6=Sat  -> we want Monday=0..Sunday=6
  const dowMon0 = (nz.getUTCDay() + 6) % 7;
  const monday = new Date(
    Date.UTC(nz.getUTCFullYear(), nz.getUTCMonth(), nz.getUTCDate())
  );
  monday.setUTCDate(monday.getUTCDate() - dowMon0);
  // Normalize to midnight
  monday.setUTCHours(0, 0, 0, 0);
  return monday.getTime(); // compare by epoch (safe)
}

function weekdayIndexNZ(dateUtc) {
  // Returns 0..6 for Mon..Sun in NZ
  const nz = toZoned(dateUtc);
  return (nz.getUTCDay() + 6) % 7;
}

const ChartDashboard = ({ orders }) => {
  console.log({ orders });
  const [tabValue, setTabValue] = useState(0);
  const [awaitRender, setAwaitRender] = useState(true);

  useEffect(() => {
    setAwaitRender(false);
  }, []);

  if (awaitRender) {
    return <div className="text-center py-12 text-gray-300">Loading...</div>;
  }

  const labels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  // Build buckets
  const thisWeekNew = Array(7).fill(0);
  const thisWeekClosed = Array(7).fill(0);
  const historyNew = Array(7).fill(0);
  const historyClosed = Array(7).fill(0);

  const now = new Date();
  const thisWeekStartKey = startOfWeekNZ(now);

  let overviewThisWeek = {
    "new-issues": 0,
    "closed-issues": 0,
    fixed: 0,
    "wont-fix": 0,
    "re-opened": 0,
    "needs-triage": 0,
  };
  let overviewHistory = {
    "new-issues": 0,
    "closed-issues": 0,
    fixed: 0,
    "wont-fix": 0,
    "re-opened": 0,
    "needs-triage": 0,
  };

  // Treat "New" = created orders; "Closed" = paid/completed orders
  const isClosed = (o) =>
    String(o?.payments?.status || "").toLowerCase() === "completed";

  (orders?.orders || orders || []).forEach((o) => {
    if (!o?.createdAt) return;
    const createdUtc = new Date(o.createdAt);
    const weekKey = startOfWeekNZ(createdUtc);
    const idx = weekdayIndexNZ(createdUtc);

    if (weekKey === thisWeekStartKey) {
      thisWeekNew[idx] += 1;
      overviewThisWeek["new-issues"] += 1;
      if (isClosed(o)) {
        thisWeekClosed[idx] += 1;
        overviewThisWeek["closed-issues"] += 1;
      }
    } else {
      historyNew[idx] += 1;
      overviewHistory["new-issues"] += 1;
      if (isClosed(o)) {
        historyClosed[idx] += 1;
        overviewHistory["closed-issues"] += 1;
      }
    }
  });

  // Use these in your UI
  const series = {
    "This Week": [
      { name: "New", data: thisWeekNew },
      { name: "Closed", data: thisWeekClosed },
    ],
    History: [
      { name: "New", data: historyNew },
      { name: "Closed", data: historyClosed },
    ],
  };

  const overview = {
    "This Week": overviewThisWeek,
    History: overviewHistory,
  };

  const ranges = {
    "This Week": "This Week",
    History: "History",
  };

  // const series = {
  //   "This Week": [
  //     { name: "New", data: [42, 28, 43, 34, 20, 25, 22] },
  //     { name: "Closed", data: [10, 10, 8, 11, 10, 10, 16] },
  //   ],
  //   "Last Week": [
  //     { name: "New", data: [37, 32, 39, 27, 18, 24, 20] },
  //     { name: "Closed", data: [10, 9, 11, 13, 8, 11, 15] },
  //   ],
  // };

  // const overview = {
  //   "This Week": {
  //     "new-issues": 214,
  //     "closed-issues": 75,
  //     fixed: 3,
  //     "wont-fix": 4,
  //     "re-opened": 8,
  //     "needs-triage": 6,
  //   },
  //   "Last Week": {
  //     "new-issues": 197,
  //     "closed-issues": 72,
  //     fixed: 6,
  //     "wont-fix": 11,
  //     "re-opened": 6,
  //     "needs-triage": 5,
  //   },
  // };

  // const ranges = {
  //   "This Week": "This Week",
  //   "Last Week": "Last Week",
  // };

  const currentRange = Object.keys(ranges)[tabValue];

  const chartOptions = {
    chart: {
      fontFamily: "inherit",
      foreColor: "#d1d5db", // text-gray-300
      type: "line",
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    colors: ["#f87171", "#facc15"],
    labels,
    dataLabels: {
      enabled: true,
      enabledOnSeries: [0],
      style: { colors: ["#fff"] },
      background: { borderWidth: 0 },
    },
    grid: { borderColor: "#374151" },
    legend: { show: false },
    plotOptions: {
      bar: { columnWidth: "50%" },
    },
    stroke: { width: [3, 0] },
    tooltip: { followCursor: true, theme: "dark" },
    xaxis: {
      categories: labels,
      axisBorder: { show: false },
      axisTicks: { color: "#4b5563" },
      labels: { style: { colors: "#9ca3af" } },
    },
    yaxis: {
      labels: {
        offsetX: -16,
        style: { colors: "#9ca3af" },
      },
    },
  };

  const overviewCards = [
    { key: "fixed", label: "Fixed" },
    { key: "wont-fix", label: "Won't Fix" },
    { key: "re-opened", label: "Re-opened" },
    { key: "needs-triage", label: "Needs Triage" },
  ];

  return (
    <div className="bg-dry border border-border text-gray-200 rounded-xl shadow p-6 w-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <h2 className="text-lg font-semibold">Old | New Customers</h2>
        <div className="flex gap-2 mt-4 sm:mt-0">
          {Object.entries(ranges).map(([key, label], index) => (
            <button
              key={key}
              onClick={() => setTabValue(index)}
              className={`px-4 py-1 rounded-full text-sm font-medium ${
                tabValue === index
                  ? "bg-white text-gray-900"
                  : "bg-gray-700 text-gray-300"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chart */}
        <div>
          <h4 className="text-sm font-medium text-gray-400 mb-3">
            New vs. Closed
          </h4>
          <ReactApexChart
            options={chartOptions}
            series={_.cloneDeep(series[currentRange])}
            type="line"
            height={320}
          />
        </div>

        {/* Overview */}
        <div>
          <h4 className="text-sm font-medium text-gray-400 mb-3">Overview</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {/* New Issues */}
            <div className="col-span-2 flex flex-col items-center justify-center rounded-xl bg-red-800 text-white p-6">
              <p className="text-5xl font-bold">
                {overview[currentRange]["new-issues"]}
              </p>
              <p className="mt-2 text-sm font-medium">New Issues</p>
            </div>

            {/* Closed Issues */}
            <div className="col-span-2 flex flex-col items-center justify-center rounded-xl bg-red-500 text-white p-6">
              <p className="text-5xl font-bold">
                {overview[currentRange]["closed-issues"]}
              </p>
              <p className="mt-2 text-sm font-medium">Closed</p>
            </div>

            {/* Dynamic cards */}
            {overviewCards.map(({ key, label }) => (
              <div
                key={key}
                className="col-span-1 flex flex-col items-center justify-center rounded-xl bg-gray-800 text-gray-200 p-6"
              >
                <p className="text-3xl font-bold">
                  {overview[currentRange][key]}
                </p>
                <p className="mt-2 text-sm font-medium text-center">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartDashboard;
